import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Star } from "lucide-react";
import { api, thumbUrl } from "@/api";
import { useFavorites } from "@/hooks/useFavorites";
import { StickerGrid } from "@/components/StickerGrid";
import { usePagedDetail, LoadMore } from "@/lib/paged";

export function Packs() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/packs").then((res) => setPacks(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="animate-spin text-[#C58B90]" size={32} />
      </div>
    );

  return (
    <div className="space-y-6">
      <h1 className="font-serif text-3xl">Packs & Coleções</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {packs.map((p, i) => {
          const cover = p.cover_sticker ? thumbUrl(p.cover_sticker.storage_path) : null;
          return (
            <button
              key={p.id}
              data-testid={`pack-card-${p.id}`}
              onClick={() => navigate(`/packs/${p.id}`)}
              className="animate-float-up text-left bg-white rounded-3xl overflow-hidden border border-[#F2E6E7] bc-shadow-soft transition-transform duration-300 hover:-translate-y-1"
              style={{ animationDelay: `${Math.min(i * 50, 400)}ms` }}
            >
              <div className="relative h-44 sticker-canvas flex items-center justify-center">
                {p.featured && (
                  <span className="absolute top-3 left-3 z-10 flex items-center gap-1 bg-[#C58B90] text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest">
                    <Star size={10} className="fill-white" /> Destaque
                  </span>
                )}
                {cover ? (
                  <img src={cover} alt={p.name} className="h-[80%] object-contain drop-shadow" />
                ) : (
                  <span className="text-6xl">{p.emoji}</span>
                )}
              </div>
              <div className="p-5">
                <p className="font-serif text-xl text-[#2C2A29]">
                  {p.emoji} {p.name}
                </p>
                {p.description && <p className="text-sm text-[#7A7571] mt-1 line-clamp-2">{p.description}</p>}
                <p className="text-xs text-[#C58B90] font-medium mt-2">{p.sticker_count} stickers</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PackDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: pack, loading, hasMore, loadingMore, loadMore } = usePagedDetail(`/packs/${id}`);
  const { favIds, onToggleFav } = useFavorites();

  if (loading)
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="animate-spin text-[#C58B90]" size={32} />
      </div>
    );
  if (!pack) return <p className="py-16 text-center text-[#7A7571]">Pack não encontrado.</p>;

  return (
    <div className="space-y-6">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#C58B90] font-medium">
        <ArrowLeft size={17} /> Voltar
      </button>
      <div>
        <h1 className="font-serif text-3xl">
          {pack.emoji} {pack.name}
        </h1>
        {pack.description && <p className="text-[#7A7571] mt-1">{pack.description}</p>}
        <p className="text-sm text-[#C58B90] font-medium mt-1">{pack.sticker_count} stickers</p>
      </div>
      <StickerGrid stickers={pack.stickers} favIds={favIds} onToggleFav={onToggleFav} />
      <LoadMore hasMore={hasMore} loading={loadingMore} onMore={loadMore} />
    </div>
  );
}
